import React, { useEffect } from 'react';
import { RouterProvider, createRouter, ErrorComponent, Link, useNavigate } from '@tanstack/react-router';
import { routeTree } from '@/routeTree.gen';
import { queryClient } from '@/contexts/ReactQuery';
import { captureException } from '@/lib/sentry';
import { Button, Result, Spin } from 'antd';
import { useAuth } from '@/hooks/useAuth';

function RouteErrorFallback({ error }: { error: Error }) {
  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <Result
      status="500"
      title="Sorry, Something went wrong"
      extra={
        <>
          <ErrorComponent error={error} />
          <Link to={'/' as any}>
            <Button type="primary">Back to home</Button>
          </Link>
        </>
      }
    />
  );
}

// Router instance - created lazily and updated when route tree changes
// Virtual file routes are automatically included in routeTree.gen.ts
let router: ReturnType<typeof createRouter> | null = null;
let lastRouteTree: typeof routeTree | null = null;

function getRouter() {
  // Update router if route tree has changed (for HMR)
  if (!router || lastRouteTree !== routeTree) {
    lastRouteTree = routeTree;
    // Route tree now includes both file-based routes and virtual routes (module routes)
    // Virtual routes are automatically merged by TanStack Router plugin
    router = createRouter({
      routeTree,
      context: {
        queryClient,
        auth: undefined!
      },
      defaultPendingComponent: () => (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <Spin size="large" />
        </div>
      ),
      defaultErrorComponent: ({ error }) => <RouteErrorFallback error={error} />,
      defaultNotFoundComponent: () => {
        const navigate = useNavigate();
        return (
          <Result
            status="404"
            title="404"
            subTitle="Sorry, the page you visited does not exist."
            extra={
              <Button onClick={() => navigate({ to: '/' })} type="primary">
                Back Home
              </Button>
            }
          />
        );
      },

      defaultPreload: 'intent',
      // Since we're using React Query, we don't want loader calls to ever be stale
      // This will ensure that the loader is always called when the route is preloaded or visited
      defaultPreloadStaleTime: 0,
      scrollRestoration: true
    } as any);
  }
  return router;
}

// Export router instance for use in AdminClientsContext
export function getRouterInstance() {
  return getRouter();
}

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

export const TanStackRouterProvider: React.FC = () => {
  const { payload, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Spin size="large" />
      </div>
    );
  }

  // Build router on first render (after modules are loaded)
  const routerInstance = getRouter();

  return <RouterProvider router={routerInstance} context={{ auth: payload }} />;
};
